import { useEffect } from "react";
import BaseDialog from "./BaseDialog";

interface IDeleteDialog {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onDelete: () => any;
  cancelLabel?: string;
  confirmLabel?: string;
  title?: string | React.ReactNode;
  text?: string | React.ReactNode;
  // Rendered as a sibling of the message, for controls the confirmation needs
  // (e.g. the "delete usage history too" option).
  extra?: React.ReactNode;
  isLoading?: boolean;
  // Keep Cancel clickable while the delete runs (LinkedAccounts disconnecting); it locks by default.
  cancelWhileLoading?: boolean;
}

/**
 * The one way to confirm a destructive action (delete, remove) before it runs. Use it instead of DeleteModal, CaseDeleteDialog or a ConfirmationDialog with a destructive confirm button. Enter confirms, Escape cancels.
 *
 * @import import { DeleteDialog } from "@/components/Modals/DeleteDialog";
 */
export function DeleteDialog({
  isOpen,
  onOpenChange,
  onDelete,
  cancelLabel = "Cancel",
  confirmLabel = "Delete",
  title,
  text,
  extra,
  isLoading,
  cancelWhileLoading = false,
}: IDeleteDialog) {
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Enter" && !isLoading) {
        onDelete();
      } else if (event.key === "Escape") {
        onOpenChange(false);
      }
    };

    // Deferred so the Enter that opened the dialog (e.g. on a menu item) cannot also confirm it.
    const listen = setTimeout(() =>
      window.addEventListener("keydown", handleKeyDown)
    );

    return () => {
      clearTimeout(listen);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onDelete, onOpenChange, isLoading]);

  return (
    <BaseDialog
      title={title || "Are you sure you want to delete?"}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      size="md"
      compact
      footerActions={[
        {
          label: cancelLabel,
          props: {
            variant: "ghost",
            onClick: () => onOpenChange(false),
            isDisabled: isLoading && !cancelWhileLoading,
          },
        },
        {
          label: confirmLabel,
          props: {
            variant: "destructive",
            onClick: onDelete,
            loading: isLoading,
          },
        },
      ]}
    >
      <p className="text-sm text-muted-foreground">
        {text || "You won't be able to revert this action."}
      </p>
      {extra}
    </BaseDialog>
  );
}
